import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import WizardProgressIndicator from 'components/ui/WizardProgressIndicator';
import Icon from 'components/AppIcon';

const CalculationProcessing = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [progress, setProgress] = useState(0);



  const processingSteps = [
    {
      id: 1,
      title: "Initializing Calculation Engine",
      description: "Setting up processing parameters and validating input data",
      icon: "Settings",
      duration: 800
    },
    {
      id: 2,
      title: "Calculating Standard Process Model",
      description: "Computing traditional processing costs with transportation estimates",
      icon: "Calculator",
      duration: 1000
    },
    {
      id: 3,
      title: "Computing Loopt Foundation Results",
      description: "Analyzing sustainable processing alternatives and tax implications",
      icon: "Recycle",
      duration: 900
    },
    {
      id: 4,
      title: "Generating Revenue Projections",
      description: "Calculating Rag Trader and Value Channel revenue opportunities",
      icon: "TrendingUp",
      duration: 700
    },
    {
      id: 5,
      title: "Finalizing Results",
      description: "Preparing comprehensive analysis dashboard",
      icon: "CheckCircle",
      duration: 600
    }
  ];



  useEffect(() => {
    let animationFrameId;
    let timeoutIds = [];

    const startProcessingAnimation = () => {
      setCurrentStep(0);
      setProgress(0);

      // Process each step sequentially
      processingSteps.forEach((step, index) => {
        const stepStartTime = processingSteps.slice(0, index).reduce((total, s) => total + s.duration, 0);

        // Start step
        const startTimeout = setTimeout(() => {
          setCurrentStep(index);
        }, stepStartTime);
        timeoutIds.push(startTimeout);

        // Animate progress during step
        const progressStartTime = stepStartTime + 100; // Small delay after step starts
        const progressTimeout = setTimeout(() => {
          animateProgressForStep(index, step.duration - 200); // Leave time for completion
        }, progressStartTime);
        timeoutIds.push(progressTimeout);
      });

      // Complete all processing and navigate
      const totalDuration = processingSteps.reduce((total, step) => total + step.duration, 0);
      const completeTimeout = setTimeout(() => {
        setProgress(100);
        setCurrentStep(processingSteps.length);

        // Navigate to results after showing completion
        const navTimeout = setTimeout(() => {
          navigate('/results-dashboard');
        }, 800);
        timeoutIds.push(navTimeout);
      }, totalDuration);
      timeoutIds.push(completeTimeout);
    };

    const animateProgressForStep = (stepIndex, duration) => {
      const startProgress = (stepIndex / processingSteps.length) * 100;
      const endProgress = ((stepIndex + 1) / processingSteps.length) * 100;
      const startTime = performance.now();

      const animate = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progressRatio = Math.min(elapsed / duration, 1);

        // Smooth easing function
        const eased = progressRatio < 0.5
          ? 4 * progressRatio * progressRatio * progressRatio
          : 1 - Math.pow(-2 * progressRatio + 2, 3) / 2;

        const currentProgress = startProgress + (endProgress - startProgress) * eased;
        setProgress(currentProgress);

        if (progressRatio < 1) {
          animationFrameId = requestAnimationFrame(animate);
        }
      };

      animationFrameId = requestAnimationFrame(animate);
    };

    // Start the animation sequence
    const initialDelay = setTimeout(() => {
      startProcessingAnimation();
    }, 500); // Brief delay before starting
    timeoutIds.push(initialDelay);

    // Cleanup function
    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
      timeoutIds.forEach(id => clearTimeout(id));
    };
  }, [navigate]);

  return (
    <div className="min-h-screen">
      <WizardProgressIndicator />

      <main className="pt-8 pb-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Processing Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-heading-bold text-text-primary mb-4">
              Processing Your Calculation
            </h1>
            <p className="text-lg text-text-secondary max-w-2xl mx-auto">
              Our advanced calculation engine is analyzing your textile processing requirements and generating comprehensive cost projections.
            </p>
          </div>

          {/* Main Processing Card */}
          <div className="bg-surface rounded-lg border border-border shadow-elevation-2 overflow-hidden mb-8">
            {/* Progress Header */}
            <div className="bg-primary-50 border-b border-primary-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                    <Icon name="Cog" size={20} color="white" className="animate-spin" />
                  </div>
                  <div>
                    <h2 className="text-lg font-heading-semibold text-primary">
                      Calculation in Progress
                    </h2>
                    <p className="text-sm text-primary-600">
                      {Math.round(progress)}% Complete
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-data text-primary">
                    {Math.round(progress)}%
                  </div>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="w-full bg-primary-100 rounded-full h-3">
                <div
                  className="bg-primary h-3 rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>

            {/* Current Step Display */}
            <div className="p-6">
              {currentStep < processingSteps.length && (
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-12 h-12 bg-accent-100 rounded-full flex items-center justify-center">
                    <Icon
                      name={processingSteps[currentStep].icon}
                      size={24}
                      className="text-accent"
                    />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-heading-semibold text-text-primary mb-1">
                      {processingSteps[currentStep].title}
                    </h3>
                    <p className="text-sm text-text-secondary">
                      {processingSteps[currentStep].description}
                    </p>
                  </div>
                  <div className="animate-pulse-subtle">
                    <Icon name="Loader2" size={20} className="text-primary animate-spin" />
                  </div>
                </div>
              )}

              {/* Processing Steps List */}
              <div className="space-y-3">
                {processingSteps.map((step, index) => (
                  <div
                    key={step.id}
                    className={`flex items-center space-x-3 p-3 rounded-lg transition-all duration-300 ${
                      index < currentStep
                        ? 'bg-success-50 border border-success-200'
                        : index === currentStep
                        ? 'bg-primary-50 border border-primary-200' :'bg-surface-50 border border-border-light'
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      index < currentStep
                        ? 'bg-success text-white'
                        : index === currentStep
                        ? 'bg-primary text-white' :'bg-surface-100 text-text-tertiary'
                    }`}>
                      {index < currentStep ? (
                        <Icon name="Check" size={16} />
                      ) : index === currentStep ? (
                        <Icon name="Loader2" size={16} className="animate-spin" />
                      ) : (
                        <Icon name={step.icon} size={16} />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className={`text-sm font-body-medium ${
                        index <= currentStep ? 'text-text-primary' : 'text-text-tertiary'
                      }`}>
                        {step.title}
                      </div>
                    </div>
                    {index < currentStep && (
                      <Icon name="CheckCircle" size={16} className="text-success" />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>



          {/* Processing Note */}
          <div className="mt-8 text-center">
            <div className="inline-flex items-center space-x-2 text-sm text-text-secondary bg-surface-50 rounded-full px-4 py-2 border border-border-light">
              <Icon name="Info" size={16} />
              <span>Please do not navigate away during processing to ensure calculation integrity</span>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CalculationProcessing;